"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTransition } from "react";
import {
  INVENTORY_FILTERS,
  type FilterDef,
  type InventoryFilter,
  type ToggleDef,
} from "@platform/schema";

export interface FacetOptions {
  make: string[];
  model: string[];
  exteriorColor: string[];
  interiorColor: string[];
}

export function FilterPanel({
  enabledKeys,
  facetOptions,
  totalCount,
}: {
  enabledKeys: string[];
  facetOptions: FacetOptions;
  totalCount: number;
}) {
  const params = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [pending, start] = useTransition();

  const enabledSet = new Set(enabledKeys);
  const visible = INVENTORY_FILTERS.filter((f) => enabledSet.has(f.key));

  function paramArray(key: string): string[] {
    const raw = params.get(key);
    return raw ? raw.split(",").filter(Boolean) : [];
  }

  function paramString(key: string): string {
    return params.get(key) ?? "";
  }

  function navigate(next: URLSearchParams) {
    next.delete("page");
    start(() => router.replace(`${pathname}?${next.toString()}`, { scroll: false }));
  }

  function setMulti(key: string, value: string, checked: boolean) {
    const next = new URLSearchParams(params.toString());
    const cur = new Set(paramArray(key));
    if (checked) {
      cur.add(value);
    } else {
      cur.delete(value);
    }
    if (cur.size === 0) {
      next.delete(key);
    } else {
      next.set(key, [...cur].join(","));
    }
    navigate(next);
  }

  function setRange(key: string, value: string) {
    const next = new URLSearchParams(params.toString());
    if (value.length === 0) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    navigate(next);
  }

  function clearAll() {
    const sort = params.get("sort");
    const next = new URLSearchParams();
    if (sort) {
      next.set("sort", sort);
    }
    navigate(next);
  }

  return (
    <aside className="flex flex-col gap-6 lg:sticky lg:top-20 lg:self-start">
      <div className="flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-[0.2em] text-[--color-foreground-subtle]">
          Filters
        </h2>
        <div className="flex items-center gap-2 text-xs text-[--color-foreground-muted]">
          {pending ? <span className="animate-pulse">Updating…</span> : null}
          <button type="button" onClick={clearAll} className="underline hover:text-foreground">
            Clear all
          </button>
        </div>
      </div>
      <p className="-mt-3 text-xs text-[--color-foreground-subtle]">
        {totalCount} matching {totalCount === 1 ? "vehicle" : "vehicles"}
      </p>

      {visible.map((f) => (
        <FilterWidget
          key={f.key}
          def={f}
          facetOptions={facetOptions}
          paramArray={paramArray}
          paramString={paramString}
          onMulti={setMulti}
          onRange={setRange}
        />
      ))}
    </aside>
  );
}

function FilterWidget({
  def,
  facetOptions,
  paramArray,
  paramString,
  onMulti,
  onRange,
}: {
  def: InventoryFilter;
  facetOptions: FacetOptions;
  paramArray: (k: string) => string[];
  paramString: (k: string) => string;
  onMulti: (k: string, v: string, checked: boolean) => void;
  onRange: (k: string, v: string) => void;
}) {
  switch (def.type) {
    case "multiselect-static":
      return (
        <Multiselect
          def={def}
          options={def.options}
          selected={paramArray(def.key)}
          onMulti={onMulti}
        />
      );
    case "multiselect-distinct":
    case "multiselect-dependent": {
      const opts = (facetOptions as unknown as Record<string, string[]>)[def.key] ?? [];
      return (
        <Multiselect def={def} options={opts} selected={paramArray(def.key)} onMulti={onMulti} />
      );
    }
    case "multiselect-features":
      // Features panel: defer (would dwarf the sidebar). Slice B can render
      // the FEATURE_TAXONOMY-driven group; for now skip rather than half-ship.
      return null;
    case "range-currency":
      return (
        <RangeWidget
          def={def}
          minKey="priceMin"
          maxKey="priceMax"
          minValue={paramString("priceMin")}
          maxValue={paramString("priceMax")}
          onRange={onRange}
          step={500}
          label="$"
        />
      );
    case "range-int": {
      const keyMap: Record<string, { minKey: string; maxKey: string; step: number }> = {
        year: { minKey: "yearMin", maxKey: "yearMax", step: 1 },
        mileage: { minKey: "mileageMin", maxKey: "mileageMax", step: 1000 },
        cityMpg: { minKey: "cityMpgMin", maxKey: "cityMpgMax", step: 1 },
        highwayMpg: { minKey: "highwayMpgMin", maxKey: "highwayMpgMax", step: 1 },
      };
      const mapped = keyMap[def.key];
      if (!mapped) {
        return null;
      }
      return (
        <RangeWidget
          def={def}
          minKey={mapped.minKey}
          maxKey={mapped.maxKey}
          minValue={paramString(mapped.minKey)}
          maxValue={paramString(mapped.maxKey)}
          onRange={onRange}
          step={mapped.step}
        />
      );
    }
    case "toggle":
      return (
        <ToggleWidget
          def={def}
          active={paramString(def.key) === "1"}
          onToggle={(on) => onRange(def.key, on ? "1" : "")}
        />
      );
    case "text-exact":
      return <TextWidget def={def} value={paramString(def.key)} onRange={onRange} />;
    default:
      return null;
  }
}

function Multiselect({
  def,
  options,
  selected,
  onMulti,
}: {
  def: FilterDef;
  options: readonly string[];
  selected: string[];
  onMulti: (k: string, v: string, checked: boolean) => void;
}) {
  if (options.length === 0) {
    return null;
  }
  const selSet = new Set(selected);
  return (
    <fieldset>
      <legend className="mb-2 text-[10px] uppercase tracking-[0.2em] text-[--color-foreground-subtle]">
        {def.label}
      </legend>
      <div className="flex max-h-48 flex-col gap-1 overflow-y-auto pr-1">
        {options.map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selSet.has(o)}
              onChange={(e) => onMulti(def.key, o, e.currentTarget.checked)}
            />
            {o}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function RangeWidget({
  def,
  minKey,
  maxKey,
  minValue,
  maxValue,
  onRange,
  step,
  label,
}: {
  def: FilterDef;
  minKey: string;
  maxKey: string;
  minValue: string;
  maxValue: string;
  onRange: (k: string, v: string) => void;
  step: number;
  label?: string;
}) {
  return (
    <fieldset>
      <legend className="mb-2 text-[10px] uppercase tracking-[0.2em] text-[--color-foreground-subtle]">
        {def.label}
      </legend>
      <div className="grid grid-cols-2 gap-2">
        <RangeInput
          aria-label={`${def.label} min`}
          placeholder={label ? `Min ${label}` : "Min"}
          step={step}
          defaultValue={minValue}
          onCommit={(v) => onRange(minKey, v)}
        />
        <RangeInput
          aria-label={`${def.label} max`}
          placeholder={label ? `Max ${label}` : "Max"}
          step={step}
          defaultValue={maxValue}
          onCommit={(v) => onRange(maxKey, v)}
        />
      </div>
    </fieldset>
  );
}

function RangeInput({
  defaultValue,
  onCommit,
  step,
  ...rest
}: {
  defaultValue: string;
  onCommit: (v: string) => void;
  step: number;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="number"
      step={step}
      min={0}
      defaultValue={defaultValue}
      onBlur={(e) => onCommit(e.currentTarget.value.trim())}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onCommit((e.target as HTMLInputElement).value.trim());
        }
      }}
      {...rest}
      className="rounded-[--radius-sm] border border-[--color-border-strong] bg-[--color-background] px-2 py-1.5 text-sm"
    />
  );
}

function TextWidget({
  def,
  value,
  onRange,
}: {
  def: FilterDef;
  value: string;
  onRange: (k: string, v: string) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-2 text-[10px] uppercase tracking-[0.2em] text-[--color-foreground-subtle]">
        {def.label}
      </legend>
      <input
        type="text"
        defaultValue={value}
        onBlur={(e) => onRange(def.key, e.currentTarget.value.trim())}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onRange(def.key, (e.target as HTMLInputElement).value.trim());
          }
        }}
        className="w-full rounded-[--radius-sm] border border-[--color-border-strong] bg-[--color-background] px-2 py-1.5 text-sm"
      />
    </fieldset>
  );
}

function ToggleWidget({
  def,
  active,
  onToggle,
}: {
  def: ToggleDef;
  active: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={active} onChange={(e) => onToggle(e.currentTarget.checked)} />
      {def.label}
    </label>
  );
}
